<template>
  <article id="fusion-image">
    <h1>图像融合</h1>

    <section id="fusion-intro">
      <h2>一、简介</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;图像融合是将多源遥感影像（如全色影像和多光谱影像）合成为一幅综合影像的过程，以提高影像的空间分辨率和光谱信息。本模块通过自动提取影像的定标参数，实现快速、准确的图像融合处理，是获取高质量遥感数据的关键步骤。
      </p>
    </section>

    <section id="fusion-interface">
      <h2>二、功能界面</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/ronghe.png" alt="图像融合界面" class="software-image" />
          <p class="image-caption">图2：图像融合界面 - 将全色影像与多光谱影像融合成高分辨率综合影像</p>
        </div>
      </div>
      <p>📷 界面组成如下：</p>
      <ul>
        <li>全色影像输入框（支持单景影像）</li>
        <li>多光谱影像输入框（支持单景影像）</li>
        <li>输出影像路径设置</li>
        <li>"打开"按钮用于选择影像文件</li>
        <li>"保存"按钮用于保存融合结果</li>
        <li>"处理"按钮启动融合处理</li>
        <li>"退出"按钮关闭程序</li>
        <li>"帮助"按钮提供操作指引</li>
      </ul>
      <p class="note">🔄 图像融合模块自动处理全色与多光谱影像的融合，提升影像空间分辨率和光谱信息质量。</p>
    </section>

    <section id="fusion-params">
      <h2>三、用户参数</h2>
      <table>
        <thead>
          <tr>
            <th>参数项</th>
            <th>含义</th>
            <th>设置方式</th>
            <th>示例值</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>全色影像路径</td>
            <td>高分辨率全色影像所在路径</td>
            <td>自动或手动选择</td>
            <td>D:\data\GF1\pan.tif</td>
          </tr>
          <tr>
            <td>多光谱影像路径</td>
            <td>多光谱影像所在路径</td>
            <td>自动或手动选择</td>
            <td>D:\data\GF1\ms.tif</td>
          </tr>
          <tr>
            <td>输出路径</td>
            <td>融合结果保存路径</td>
            <td>手动选择</td>
            <td>D:\data\GF1\fusion\</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅ 注意：所有参数均可自动完成配置，用户无需手动输入</p>
    </section>

    <section id="fusion-steps">
      <h2>四、操作步骤</h2>
      <ol>
        <li>
          <strong>导入全色影像</strong>
          <ul>
            <li>点击"打开"按钮选择全色影像路径；</li>
            <li>支持格式 GeoTIFF。</li>
          </ul>
        </li>
        <li>
          <strong>导入多光谱影像</strong>
          <ul>
            <li>点击"打开"按钮选择多光谱影像路径；</li>
            <li>支持格式 GeoTIFF。</li>
          </ul>
        </li>
        <li>
          <strong>自动识别元数据</strong>
          <ul>
            <li>软件将自动解析影像头文件或元数据 XML，识别卫星平台、传感器、拍摄时间等。</li>
          </ul>
        </li>
        <li>
          <strong>提取融合参数</strong>
          <ul>
            <li>软件调用内置"影像融合参数库"，匹配该影像对应的融合算法。</li>
          </ul>
        </li>
        <li>
          <strong>执行图像融合</strong>
          <ul>
            <li>按照选定的融合算法，逐波段完成融合；</li>
            <li>支持 CPU 多线程加速与内存优化处理。</li>
          </ul>
        </li>
        <li>
          <strong>保存输出结果</strong>
          <ul>
            <li>输出格式为 GeoTIFF，命名方式与原始影像一致。</li>
          </ul>
        </li>
      </ol>
    </section>

    <section id="fusion-results">
      <h2>五、用户结果</h2>
      <table>
        <thead>
          <tr>
            <th>输出文件名</th>
            <th>文件类型</th>
            <th>描述说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GF1_WFV_20230510_L1_fused.tif</td>
            <td>影像文件</td>
            <td>融合后高分辨率综合影像，包含丰富的光谱信息</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L1_fused.log</td>
            <td>日志文件</td>
            <td>记录融合处理过程、算法参数及处理时间等信息</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅ 输出影像可直接作为后续"大气校正模块"或"地表反射率"模块的输入文件。</p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'FusionImage',
  props: {
    targetSectionId: {
      type: String,
      default: null,
    },
  },
  watch: {
    targetSectionId(newId) {
      if (newId) {
        this.$emit('scroll-to-section', newId);
      }
    },
  },
  mounted() {
    if (this.targetSectionId) {
      this.$emit('scroll-to-section', this.targetSectionId);
    }
  },
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 添加计数器重置 */
ol {
  counter-reset: list-item;
}

/* 重用 Document.vue 中的表格和列表样式 */
.main-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.main-content th,
.main-content td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2);
  color: var(--text-primary);
}

.main-content thead tr {
  background-color: rgba(var(--primary), 0.2);
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

.main-content tbody tr {
  background-color: rgba(var(--card-bg), 0.6);
}

.main-content tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8);
}

.main-content tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
.main-content ol,
.main-content ul {
  list-style: none;
  padding-left: 20px;
  margin: 20px 0;
}

.main-content ol li,
.main-content ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

/* 新增：将 counter-increment 添加到 ol li */
ol li {
  counter-increment: list-item;
}

ol li strong,
ul li strong {
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

ol li::before {
  content: counter(list-item) ". ";
  color: var(--secondary);
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li::before {
  content: "\2705";
  color: var(--secondary);
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li p {
  margin-left: 0;
  margin-bottom: 5px;
}

.main-content p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}
</style>


